<template>
  <SelectRoot
    v-model:open="isOpen"
    :model-value="modelValue"
    :disabled="disabled"
    @update:model-value="updateValue"
  >
    <SelectTrigger
      :id="settingId"
      :class="[
        `design-system-input relative flex h-8 w-full items-center gap-2.5 overflow-hidden rounded-lg border bg-input px-2.5 text-left text-sm text-current outline-2 -outline-offset-1 outline-blue-500 focus-visible:outline`,
        props.class,
      ]"
    >
      <SelectValue
        :placeholder="placeholder ?? '&ZeroWidthSpace;'"
        class="grow truncate"
      />

      <template v-if="state">
        <template v-if="state.status === 'pending'">
          <ArrowPathIcon
            pending
            class="m-0.5 -mr-1 size-4 animate-spin text-muted-foreground"
          />
        </template>
        <template v-else-if="state.status === 'success'">
          <CheckCircleIcon class="-mr-1 size-5 text-green-500" />
        </template>
        <template v-else-if="state.status === 'error'">
          <Tooltip.Root interactive>
            <Tooltip.Trigger>
              <ExclamationCircleIcon class="-mr-1 size-5 text-red-500" />
            </Tooltip.Trigger>

            <Tooltip.Content>
              {{ state.message }}

              <RouterLink
                v-if="state.route"
                :to="state.route"
                class="mt-2 block cursor-pointer rounded bg-blue-500 px-2 py-1 text-xs font-bold uppercase text-white"
              >
                Click here
              </RouterLink>
            </Tooltip.Content>
          </Tooltip.Root>
        </template>
      </template>

      <ChevronDownIcon class="size-5 text-muted-foreground" />
    </SelectTrigger>

    <SelectPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <SelectContent
          :collision-padding="10"
          class="z-30 flex flex-col rounded-md bg-gray-900 py-2 text-white shadow-lg dark:border dark:border-border dark:bg-card"
        >
          <SelectScrollUpButton class="-mt-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 rotate-180 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollUpButton>

          <SelectViewport>
            <template
              v-for="(children, groupName, idx) in groupedOptions"
              :key="groupName"
            >
              <SelectSeparator
                v-if="idx !== 0"
                class="my-2 border-t border-gray-700 dark:border-border"
              />

              <SelectLabel
                v-if="groupName"
                class="mx-5 my-1 shrink-0 select-none border-t-border text-[0.8125rem] leading-5 text-gray-400 dark:text-muted-foreground"
              >
                {{ groupName }}
              </SelectLabel>

              <SelectGroup>
                <SelectItem
                  v-for="{
                    id,
                    label,
                    description,
                    disabled: itemDisabled,
                  } in children"
                  :key="id"
                  :value="id === '' ? INTERNAL_EMPTY_ID : id"
                  :disabled="itemDisabled"
                  class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-zinc-700 dark:data-[highlighted]:bg-muted"
                >
                  <div class="flex min-w-0 grow flex-col break-words">
                    <SelectItemText
                      class="transition-opacity group-data-[disabled]:opacity-50"
                    >
                      {{ label }}
                    </SelectItemText>

                    <p
                      v-if="description"
                      class="text-[0.8125rem] leading-5 text-gray-400"
                    >
                      {{ description }}
                    </p>
                  </div>

                  <CheckIcon
                    v-if="id !== '' || !itemDisabled"
                    class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                  />
                </SelectItem>
              </SelectGroup>
            </template>
          </SelectViewport>

          <SelectScrollDownButton class="-mb-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollDownButton>
        </SelectContent>
      </Transition>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup lang="ts" generic="T extends string = string">
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectLabel,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";
import { computed, ref, useId } from "vue";

import { useSettingControl } from "@/components/Setting/context";
import Tooltip from "@/design_system/Tooltip";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckCircleIcon from "@/icons/heroicons/check-circle-mini.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-mini.svg";
import { useKeybinds } from "@/lib/hotkeys";

import type { ValidationStatus } from "./types";

export interface SelectOption<T extends string = string> {
  id: T;
  label: string;
  description?: string;
  disabled?: boolean;
  group?: string;
}

// Radix Vue doesn't support passing an empty string value, the expectation is
// that it would be used to show the placeholder set by `<SelectValue>`.
// We'll work around this by substituting the empty string
const INTERNAL_EMPTY_ID = `__empty_${useId()}`;

const modelValue = defineModel<T>({
  default: "" as T,
  get(v): T {
    return v === "" ? (INTERNAL_EMPTY_ID as T) : v;
  },
  set(v) {
    return v === INTERNAL_EMPTY_ID ? "" : v;
  },
});

const isOpen = ref(false);

const updateValue = (next: string) => {
  modelValue.value = next as T;
};

const props = defineProps<{
  options: readonly SelectOption<T>[];
  disabled?: boolean;
  state?: ValidationStatus | null;
  class?: string;
  placeholder?: string;
}>();

const setting = useSettingControl();
const settingId = setting?.register();

const groupedOptions = computed(() => {
  const ungroupedOptions: SelectOption[] = [];
  const groups: Record<string, SelectOption[]> = {};

  for (const option of props.options) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  // Always sort ungrouped options first
  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});

useKeybinds(() => {
  if (!isOpen.value) {
    return;
  }

  return {
    Escape: {
      name: `Close select`,
      group: `Select`,
      run: (ev) => {
        if (ev.defaultPrevented) {
          return;
        }

        isOpen.value = false;
        return true;
      },
    },
  };
});
</script>
